// Lazy loader + executor for Google reCAPTCHA v3.
// The script is only fetched the first time `executeRecaptcha` is called,
// so pages without a contact form never pay the bundle cost.

interface GrecaptchaV3 {
  ready: (cb: () => void) => void
  execute: (siteKey: string, opts: {action: string}) => Promise<string>
}

declare global {
  interface Window {
    grecaptcha?: GrecaptchaV3
  }
}

const getSiteKey = () => import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY?.trim() ?? ''

let scriptPromise: Promise<void> | null = null
let missingKeyWarned = false

const loadScript = (siteKey: string): Promise<void> => {
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-recaptcha-v3]')
    if (existing) {
      if (window.grecaptcha) {
        resolve()
        return
      }
      existing.addEventListener('load', () => resolve(), {once: true})
      existing.addEventListener('error', () => reject(new Error('recaptcha_script_failed')), {once: true})
      return
    }
    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`
    script.async = true
    script.defer = true
    script.dataset.recaptchaV3 = 'true'
    script.addEventListener('load', () => resolve(), {once: true})
    script.addEventListener('error', () => reject(new Error('recaptcha_script_failed')), {once: true})
    document.head.appendChild(script)
  })
  return scriptPromise
}

const waitForReady = (grecaptcha: GrecaptchaV3) =>
  new Promise<void>((resolve) => {
    grecaptcha.ready(resolve)
  })

/**
 * Returns a fresh reCAPTCHA v3 token for the given action.
 * Returns '' when the site key is not configured (dev mode) — the BE treats an
 * empty token as a skipped verification in non-production environments.
 */
export const executeRecaptcha = async (action: string): Promise<string> => {
  const siteKey = getSiteKey()
  if (!siteKey) {
    if (!missingKeyWarned) {
      console.warn('[recaptcha] VITE_RECAPTCHA_V3_SITE_KEY not set — submitting without token (dev only).')
      missingKeyWarned = true
    }
    return ''
  }

  await loadScript(siteKey)
  const {grecaptcha} = window
  if (!grecaptcha) {
    throw new Error('recaptcha_unavailable')
  }
  await waitForReady(grecaptcha)
  return grecaptcha.execute(siteKey, {action})
}
